// Редовият модел и сумите на справка „Променени цени".
//
// BE-то не праща поле „накъде мърда цената" — посоката е ЗНАКЪТ на
// `difference` (нова − стара цена): > 0 = поскъпване, всичко останало =
// поевтиняване. Разделянето става тук веднъж и всичко нататък чете
// `direction`, вместо всеки компонент да преповтаря сравнението с нула.
//
// Втората конвенция е капан: `percent` идва по ОБРАТНАТА логика (> 0 =
// поевтиняване), тоест знакът му спори със знака на сумата. Редът го обръща
// още при строенето, за да не се налага нито една клетка да помни това.

import { parseDecimal } from "../../../../components/dataTable";
import type { PriceChangeReportItem } from "../../../../api/reportsPriceChanges";

export type PriceDirection = "up" | "down";

/** Надолу = поевтиняване (отрицателна разлика), нагоре = поскъпване. */
const priceDirectionOf = (item: PriceChangeReportItem): PriceDirection =>
  parseDecimal(item.difference) > 0 ? "up" : "down";

/**
 * Редът, както влиза в таблицата. Всички ЕТИКЕТИ (вид, посока, статус) са
 * разрешени тук, в данните — не в `accessorFn`: TanStack кешира стойността на
 * клетката за живота на `data`, а React Query пази същата референция при
 * непроменен отговор, тоест превод, сменен по-късно, никога не би стигнал до
 * клетката.
 */
export interface PriceChangeRow {
  /** Стабилен ключ — BE-то го дава готов (`row_key`). */
  rowId: string;
  billId: number;
  /** Името на масата, както го вижда сервитьорът. */
  billLabel: string;
  changedAt: string;
  userLabel: string;
  itemLabel: string;
  /** „Добавка" под името на продукта; `null` за обикновен продукт. */
  additionHint: string | null;
  kindLabel: string;
  direction: PriceDirection;
  directionLabel: string;
  statusLabel: string;
  quantity: number;
  oldPrice: number;
  newPrice: number;
  /** Знакова разлика на единица: < 0 поевтиняване, > 0 поскъпване. */
  difference: number;
  /** Процентът, ОБЪРНАТ да върви със знака на сумата. */
  percent: number;
  /** Разликата × количеството — отражението върху сметката. */
  lineDifference: number;
}

export interface PriceChangeLabels {
  product: string;
  addition: string;
  up: string;
  down: string;
  active: string;
  reverted: string;
}

export const buildPriceChangeRows = (
  items: readonly PriceChangeReportItem[],
  labels: PriceChangeLabels,
): PriceChangeRow[] =>
  items.map((item) => {
    const direction = priceDirectionOf(item);
    const isAddition = item.kind === "addition";
    return {
      rowId: item.row_key,
      billId: item.bill_id,
      billLabel: item.bill_name,
      changedAt: item.changed_at,
      userLabel: item.user_name ?? "",
      itemLabel: item.item_name,
      additionHint: isAddition ? labels.addition : null,
      kindLabel: isAddition ? labels.addition : labels.product,
      direction,
      directionLabel: direction === "up" ? labels.up : labels.down,
      statusLabel: item.is_active ? labels.active : labels.reverted,
      quantity: parseDecimal(item.quantity),
      oldPrice: parseDecimal(item.old_price),
      newPrice: parseDecimal(item.new_price),
      difference: parseDecimal(item.difference),
      // Знакът на процента от BE значи обратното на знака на сумата — обръща
      // се тук, за да четат двете колони една и съща посока.
      percent: -parseDecimal(item.percent),
      lineDifference: parseDecimal(item.line_difference),
    };
  });

export interface PriceChangesSummary {
  /** Всички промени в обхвата — поевтиняване и поскъпване заедно. */
  changes: number;
  downRows: number;
  /** Общо отнетата сума, ПОЛОЖИТЕЛНО число. */
  downTotal: number;
  upRows: number;
  /** Общо добавената сума, ПОЛОЖИТЕЛНО число. */
  upTotal: number;
}

const EMPTY_SUMMARY: PriceChangesSummary = {
  changes: 0,
  downRows: 0,
  downTotal: 0,
  upRows: 0,
  upTotal: 0,
};

/**
 * Обобщението за ПОКАЗАНИТЕ редове.
 *
 * Сумира се `lineDifference` по абсолютна стойност — отражението върху
 * сметката, не разликата на единица: два реда по 0.50 € върху 3 бройки са
 * 3.00 €, а не 1.00 €.
 */
export const summarizePriceChanges = (
  rows: readonly PriceChangeRow[],
): PriceChangesSummary => {
  if (!rows.length) return EMPTY_SUMMARY;

  const summary: PriceChangesSummary = {
    ...EMPTY_SUMMARY,
    changes: rows.length,
  };

  for (const row of rows) {
    const amount = Math.abs(row.lineDifference);
    if (row.direction === "up") {
      summary.upRows += 1;
      summary.upTotal += amount;
      continue;
    }
    summary.downRows += 1;
    summary.downTotal += amount;
  }

  return summary;
};
